import { useEffect, useState } from 'react';

import SlideoutShell, { BorderedSection } from 'components/insights/careTransitions/shared/slideout/SlideoutShell';
import { END_DATE, START_DATE } from 'constants/filterKeysConstants';
import { useBusinessDevelopmentActions, useBusinessDevelopmentStore } from 'stores/businessDevelopmentStore';

import { TABLE_PIVOT_OPTIONS, TablePivot, ViewBy } from '../constants';

import BusinessDevelopmentPatientList from './BusinessDevelopmentPatientList';
import SlideoutBreakdownTable from './SlideoutBreakdownTable';
import SlideoutTiles from './SlideoutTiles';
import useSlideoutAnalytics from './useSlideoutAnalytics';

type SlideoutMeta = {
  title: string;
  subtitle: string;
  ready: boolean;
};

export default function BusinessDevelopmentSlideout() {
  const slideoutContext = useBusinessDevelopmentStore((s) => s.slideoutContext);
  const { closeSlideout } = useBusinessDevelopmentActions();
  const startDate = useBusinessDevelopmentStore((s) => s.filters[START_DATE]) as Date | undefined;
  const endDate = useBusinessDevelopmentStore((s) => s.filters[END_DATE]) as Date | undefined;

  const [meta, setMeta] = useState<SlideoutMeta>({ title: '', subtitle: '', ready: false });

  return (
    <SlideoutShell
      open={!!slideoutContext}
      onClose={closeSlideout}
      title={meta.title}
      subtitle={meta.subtitle}
      subtitleReady={meta.ready}
      startDate={startDate}
      endDate={endDate}>
      {slideoutContext && (
        <SlideoutContent
          entityId={slideoutContext.entityId}
          entityName={slideoutContext.entityName}
          viewBy={slideoutContext.viewBy}
          tablePivot={slideoutContext.tablePivot}
          onMeta={setMeta}
        />
      )}
    </SlideoutShell>
  );
}

function SlideoutContent({
  entityId,
  entityName,
  viewBy,
  tablePivot,
  onMeta,
}: {
  entityId: string;
  entityName: string;
  viewBy: ViewBy;
  tablePivot: TablePivot;
  onMeta: (m: SlideoutMeta) => void;
}) {
  const analytics = useSlideoutAnalytics(entityId, entityName, viewBy, tablePivot);
  const {
    careTransitionsRollups,
    dischargesRollups,
    referralsRollups,
    breakdownRollups,
    viewByCounts,
    countsLoading,
    loading,
    activeOption,
    viewByOptions,
  } = analytics;

  const pivotLabel = TABLE_PIVOT_OPTIONS.find((o) => o.value === tablePivot)?.label ?? '';
  const title = `${entityName} – ${pivotLabel} Overview`;
  const pluralize = (label: string, count: number) => {
    if (count === 1) return label;
    if (label.endsWith('y')) return `${label.slice(0, -1)}ies`;
    return `${label}s`;
  };
  const subtitle = viewByCounts.map((c) => `${c.count} ${pluralize(c.label, c.count)}`).join('  •  ');
  const ready = !countsLoading;

  useEffect(() => {
    onMeta({ title, subtitle, ready });
  }, [title, subtitle, ready, onMeta]);

  return (
    <>
      <SlideoutTiles
        careTransitionsRollups={careTransitionsRollups}
        dischargesRollups={dischargesRollups}
        referralsRollups={referralsRollups}
        tablePivot={tablePivot}
        loading={loading}
      />
      <BorderedSection>
        <SlideoutBreakdownTable
          loading={loading}
          rollups={breakdownRollups}
          viewBy={viewBy}
          entityId={entityId}
          entityName={entityName}
          tablePivot={tablePivot}
          activeOption={activeOption}
          viewByOptions={viewByOptions}
        />
      </BorderedSection>
      <BorderedSection>
        <BusinessDevelopmentPatientList
          tablePivot={tablePivot}
          viewBy={viewBy}
          entityId={entityId}
          entityName={entityName}
        />
      </BorderedSection>
    </>
  );
}
